import { ORG_GEO_FIELDS, ORG_GEO_SIMS } from "constants/settings.constants";

import { IGroupByListItem } from "models";
import { IGroupColumn, ISubgroupColumn } from "models/columns";

type VisibleJobNames = Partial<Record<string, Set<string>>>;

const isOrgGeoGroup = (name: string) => name === ORG_GEO_FIELDS || name === ORG_GEO_SIMS;

export const hasOrgGeoColumns = (categories: Maybe<IGroupByListItem[]>): boolean =>
  !!categories?.some(
    (category) => isOrgGeoGroup(category.name) && category.columns?.length
  );

// Read off the tree's flag, never its key: lodash stringifies a missing subgroup
// to "undefined"/"null", which a job can be literally named.
const isSubgroupVisible = (subgroup: ISubgroupColumn, visible: Maybe<Set<string>>) =>
  !!subgroup.hasNoSubgroup || !!visible?.has(subgroup.key);

/**
 * An org-geo column's subgroup IS its job name, so only the jobs in the visible
 * set keep their columns; a group with no entry shows none (fail closed). A
 * column with no subgroup names no job and is always kept.
 */
export function withVisibleOrgGeoColumns(
  categories: IGroupByListItem[],
  visibleJobNames: VisibleJobNames
): IGroupByListItem[] {
  let changed = false;
  const next = categories.map((category) => {
    if (!isOrgGeoGroup(category.name) || !category.columns) return category;
    const visible = visibleJobNames[category.name];
    const columns = category.columns.filter(
      (column) => !column.subgroup || !!visible?.has(column.subgroup)
    );
    if (columns.length === category.columns.length) return category;
    changed = true;
    return { ...category, columns };
  });
  return changed ? next : categories;
}

/**
 * The chooser-tree counterpart. Filtering the categories before the tree is
 * built would also drop the hidden columns from the tree's property map, and a
 * column already in the user's table or saved layout would lose its header.
 */
export function withVisibleOrgGeoSubgroups(
  tree: IGroupColumn[],
  visibleJobNames: VisibleJobNames
): IGroupColumn[] {
  let changed = false;
  const next = tree.map((group) => {
    if (!isOrgGeoGroup(group.key)) return group;
    const visible = visibleJobNames[group.key];
    const subgroups = group.subgroups.filter((subgroup) =>
      isSubgroupVisible(subgroup, visible)
    );
    if (subgroups.length === group.subgroups.length) return group;
    changed = true;
    return { ...group, subgroups };
  });
  return changed ? next : tree;
}

/**
 * Properties of the org-geo job columns the chooser tree leaves out. A chooser
 * edit must carry these through from the saved selection, or a cold load, a
 * failed list or a lapse would drop them for good.
 */
export function heldBackOrgGeoPropertyKeys(
  tree: IGroupColumn[],
  visibleJobNames: VisibleJobNames
): Set<string> {
  const keys = new Set<string>();
  for (const group of tree) {
    if (!isOrgGeoGroup(group.key)) continue;
    for (const subgroup of group.subgroups) {
      if (isSubgroupVisible(subgroup, visibleJobNames[group.key])) continue;
      subgroup.columns.forEach((column) => keys.add(column.property));
    }
  }
  return keys;
}
